/**
 * Data logic of the Timeline "Tasks" board, shared by the desktop kanban
 * (`TaskKanbanView`) and the phone list (`MobileTaskList`): which projects the
 * period shows, the tasks of those projects, the priority / project / search
 * filters, the three lanes (To Do · Working on · Done) and the group-by sections.
 *
 * Pure functions only: the component owns the state (search text, filters,
 * group-by) and the task cache subscription.
 */
import type { Project, ProjectsByStatus, Task, TaskPriority } from './types';
import { categoryIcons } from './utils';

export type TaskWithProject = Task & { project: Project };

export type GroupBy = 'none' | 'priority' | 'project';

export interface TaskGroup {
  label: string;
  key: string;
  priorityKey?: TaskPriority;
  projectId?: string;
  tasks: TaskWithProject[];
}

export type BoardLane = 'todo' | 'active' | 'done';

export interface BoardFilters {
  priorities: ReadonlySet<TaskPriority>;
  projectIds: ReadonlySet<string>;
  query: string;
}

/** localStorage keys of the persisted board preferences (same for desktop and phone). */
export const GROUP_BY_STORAGE_KEY = 'taskBoard:groupBy';
export const PRIORITY_FILTER_STORAGE_KEY = 'taskBoard:priorityFilter';

export const priorityOrder: Record<TaskPriority, number> = { high: 0, medium: 1, low: 2, none: 3 };

export const priorityLabels: Record<TaskPriority, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  none: 'No Priority',
};

export const priorityDotColors: Record<TaskPriority, string> = {
  high: '#ef4444',
  medium: '#fb923c',
  low: '#60a5fa',
  none: '#d1d5db',
};

export const PRIORITIES: TaskPriority[] = ['high', 'medium', 'low', 'none'];

/** Every project the period shows (week and month data, de-duplicated by id). */
export function collectBoardProjects(
  projectsByWeek: Record<string, ProjectsByStatus> | undefined,
  currentWeek: string | undefined,
  projectsByMonth: Record<string, ProjectsByStatus> | undefined,
): Project[] {
  const projects: Project[] = [];
  const seen = new Set<string>();

  function add(p: Project) {
    if (!seen.has(p.id)) {
      seen.add(p.id);
      projects.push(p);
    }
  }

  // Week scale
  if (projectsByWeek && currentWeek) {
    const data = projectsByWeek[currentWeek];
    if (data) {
      for (const status of Object.values(data)) {
        (status as Project[]).forEach(add);
      }
    }
  }
  // Month scale: iterate ALL months in projectsByMonth
  // (for year scale this includes all 12 months; for month scale just the current one)
  if (projectsByMonth) {
    for (const data of Object.values(projectsByMonth)) {
      for (const status of Object.values(data)) {
        (status as Project[]).forEach(add);
      }
    }
  }

  return projects;
}

/** The cached tasks of the period's projects, each with its project attached. */
export function buildBoardTasks(
  projects: Project[],
  tasksByProject: Record<string, Task[]>,
): TaskWithProject[] {
  const projectMap = new Map(projects.map((p) => [p.id, p]));
  const result: TaskWithProject[] = [];

  for (const [pid, tasks] of Object.entries(tasksByProject)) {
    const project = projectMap.get(pid);
    if (!project) continue;
    for (const task of tasks) {
      result.push({ ...task, project });
    }
  }
  return result;
}

/** Apply the priority + project + search filters (each is skipped when empty). */
export function filterBoardTasks(
  tasks: TaskWithProject[],
  { priorities, projectIds, query }: BoardFilters,
): TaskWithProject[] {
  let result = tasks;
  if (priorities.size > 0) {
    result = result.filter((t) => priorities.has(t.priority));
  }
  if (projectIds.size > 0) {
    result = result.filter((t) => projectIds.has(t.projectId));
  }
  if (query.trim()) {
    const q = query.toLowerCase().trim();
    result = result.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        t.notes?.toLowerCase().includes(q) ||
        t.project.title.toLowerCase().includes(q),
    );
  }
  return result;
}

/** The kanban lanes: open + not pinned, open + pinned ("Working on"), and done / won't do. */
export function splitBoardLanes(tasks: TaskWithProject[]): Record<BoardLane, TaskWithProject[]> {
  return {
    todo: tasks.filter((t) => t.status === 'todo' && !t.isActive),
    active: tasks.filter((t) => t.status === 'todo' && t.isActive),
    done: tasks.filter((t) => t.status === 'done' || t.status === 'wont_do'),
  };
}

export function sortTasksForBoard(a: TaskWithProject, b: TaskWithProject): number {
  const pd = priorityOrder[a.priority] - priorityOrder[b.priority];
  if (pd !== 0) return pd;
  return a.position - b.position;
}

/** Sections of one lane: by priority, by project (A→Z), or one unlabeled flat list. */
export function groupBoardTasks(tasks: TaskWithProject[], groupBy: GroupBy): TaskGroup[] {
  if (groupBy === 'priority') {
    const groups: Record<string, TaskWithProject[]> = { high: [], medium: [], low: [], none: [] };
    for (const t of tasks) {
      groups[t.priority].push(t);
    }
    return PRIORITIES.filter((p) => groups[p].length > 0).map((p) => ({
      label: priorityLabels[p],
      key: p,
      priorityKey: p,
      tasks: groups[p].sort(sortTasksForBoard),
    }));
  }
  if (groupBy === 'project') {
    const groups = new Map<string, TaskWithProject[]>();
    for (const t of tasks) {
      const arr = groups.get(t.projectId) || [];
      arr.push(t);
      groups.set(t.projectId, arr);
    }
    return Array.from(groups.entries())
      .map(([pid, projectTasks]) => ({
        label: `${categoryIcons[projectTasks[0].project.category] || '📋'} ${projectTasks[0].project.title}`,
        key: pid,
        projectId: pid,
        tasks: projectTasks.sort(sortTasksForBoard),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }
  // 'none': flat list sorted by priority then position
  return [
    {
      label: '',
      key: 'all',
      tasks: [...tasks].sort(sortTasksForBoard),
    },
  ];
}

/** The lane the phone list opens on: Working on when it has tasks, else To Do. */
export function defaultBoardLane(counts: Record<BoardLane, number>): BoardLane {
  return counts.active > 0 ? 'active' : 'todo';
}
